import { useEffect } from "react";
import { Box, Grid, Typography, SxProps, Theme, List, ListItem, IconButton } from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import { useBlog } from "../hooks";
import { ElementType } from "../adapters/api";
import { CommonStyles } from "../styles/CommonStyles";
import BlogArticleCard from "../components/BlogArticleCard";
import PageTitle from "../components/PageTitle";
import PageContent from "../components/PageContent";
import { isMobile } from "react-device-detect";
import backImg from "../assets/back.webp";
import backImgMobile from "../assets/back-mobile.webp";
import { useTranslation } from "react-i18next";

const BlogDetailPageStyle: Record<string, SxProps<Theme>> = {
    blogTitle: {
        fontSize: "2rem",
        textAlign: "left",
        // marginBottom: "20px",
    },
    blogSubtitle: {
        fontSize: "1rem",
        textAlign: "left",
        color: "#bababa",
        marginTop: "30px",
    },
    blogTitleMobile: {
        fontSize: "20px",
        textAlign: "left",
        // marginBottom: "20px",
    },
    blogSubtitleMobile: {
        fontSize: "16px",
        textAlign: "left",
        color: "#bababa",
        marginTop: "30px",
    },
    blogContent: {
        padding: "20px",
        textAlign: "left",
    },
    blogContentMobile: {
        padding: "10px",
        textAlign: "left",
    },
    blogContentText: {
        fontSize: "1.2rem",
        marginBottom: "10px",
    },
    blogContentTextMobile: {
        fontSize: "16px",
        marginBottom: "10px",
    },
    blogContentList: {
        listStyle: "circle",
        pl: 4,
        fontSize: "3rem"
    },
    blogContentListItem: {
        fontSize: "1.2rem",
        display: "list-item"
    },
    blogContentListItemMobile: {
        fontSize: "16px",
        display: "list-item"
    },
    blogContentSubtitle: {
        fontSize: "1.3rem",
        marginTop: "40px",
        marginBottom: "10px",
        fontWeight: "bold"
    },
    blogContentSubtitleMobile: {
        fontSize: "18px",
        marginTop: "40px",
        marginBottom: "10px",
        fontWeight: "bold"
    },
    headerImage: {
        maxHeight: "300px",
        img: {
            height: "300px",
            width: "100%",
            objectFit: "cover",
        },
    },
    headerImageMobile: {
        maxHeight: "150px",
        img: {
            height: "150px",
            width: "100%",
            objectFit: "cover",
        },
    },
    blogContentImage: {
        marginTop: "30px",
        marginBottom: "30px",
        img: {
            width: "100%",
            height: "auto",
        },
    },
    blogContentImageMobile: {
        marginTop: "20px",
        marginBottom: "20px",
        img: {
            width: "100%",
            height: "auto",
        },
    },
    sectionTitle: (theme: Theme) => ({
        fontSize: "1.4rem",
        borderBottom: `2px solid ${theme.palette.secondary.main}`,
        marginBottom: "20px",
    }),
    sectionTitleMobile: (theme: Theme) => ({
        fontSize: "18px",
        marginBottom: "20px",
    }),
    moreContainer: {
        padding: "40px",
        width: "100%",
    },
    moreContainerMobile: {
        padding: "20px",
        width: "100%",
    },
    morePosts: {
        padding: "20px",
    },
    morePostsMobile: {
        padding: "0px",
    }
};
type BlogDetailPageParams = {
    blogId: string;
};

const BlogDetailPage = () => {
    const { blogId } = useParams<BlogDetailPageParams>();
    const { blogPost, blogMorePost } = useBlog(Number(blogId));
    let navigate = useNavigate();
    const { t } = useTranslation();

    useEffect(() => {
        window.scrollTo(0, 0);
        // eslint-disable-next-line
    }, [blogPost]);

    return (
        <>
            <Grid container>
                <IconButton
                    size="large"
                    edge="start"
                    color="inherit"
                    aria-label="menu"
                    sx={isMobile ? CommonStyles.backMobile : CommonStyles.back}
                    onClick={() => navigate("/news")}
                >
                    <img src={isMobile ? backImgMobile : backImg} />
                </IconButton>
                <PageTitle>NEWS</PageTitle>
            </Grid>
            <PageContent>
                <Grid container spacing={4}>
                    <Grid item md={12} width={"100%"}>
                        <Box
                            sx={[
                                isMobile
                                    ? CommonStyles.borderedImageMobile
                                    : CommonStyles.borderedImage,
                                isMobile
                                    ? BlogDetailPageStyle.headerImageMobile
                                    : BlogDetailPageStyle.headerImage,
                            ].flat(2)}
                        >
                            <img src={"/backend/static/" + blogPost?.coverImage} />
                        </Box>
                        <Typography
                            sx={
                                isMobile
                                    ? BlogDetailPageStyle.blogSubtitleMobile
                                    : BlogDetailPageStyle.blogSubtitle
                            }
                        >
                            {blogPost?.createdDate} | {blogPost?.createdBy}
                        </Typography>
                    </Grid>
                    <Grid item md={12}>
                        <Typography
                            sx={
                                isMobile
                                    ? BlogDetailPageStyle.blogTitleMobile
                                    : BlogDetailPageStyle.blogTitle
                            }
                        >
                            {blogPost?.title}
                        </Typography>
                        <Box
                            sx={
                                isMobile
                                    ? BlogDetailPageStyle.blogContentMobile
                                    : BlogDetailPageStyle.blogContent
                            }
                        >
                            {blogPost?.content.map((item) => {

                                if (item.type === ElementType.Image) {
                                    return (
                                        <Grid container>
                                            <Grid item md={3} sm={0}></Grid>
                                            <Grid
                                                item
                                                md={6}
                                                sm={12}
                                                sx={
                                                    isMobile
                                                        ? BlogDetailPageStyle.blogContentImageMobile
                                                        : BlogDetailPageStyle.blogContentImage
                                                }
                                            >
                                                <img src={"/backend/static/" + item.data!} />
                                                {
                                                    item.caption && <div>{item.caption}</div>
                                                }
                                            </Grid>
                                            <Grid item md={3} sm={0}></Grid>
                                        </Grid>
                                    );
                                }
                                if (item.type === ElementType.Subtitle) {
                                    return <Typography
                                        sx={
                                            isMobile
                                                ? BlogDetailPageStyle.blogContentSubtitleMobile
                                                : BlogDetailPageStyle.blogContentSubtitle
                                        }
                                    >
                                        {item.data}
                                    </Typography>;
                                }
                                if (item.type === ElementType.Paragraph) {
                                    return (
                                        <Typography
                                            sx={
                                                isMobile
                                                    ? BlogDetailPageStyle.blogContentTextMobile
                                                    : BlogDetailPageStyle.blogContentText
                                            }
                                        >
                                            {item.data}
                                        </Typography>
                                    );
                                }
                                if (item.type === ElementType.UList) {
                                    return <List sx={BlogDetailPageStyle.blogContentList} >
                                        {
                                            item.children?.map(listItem => <ListItem sx={
                                                isMobile
                                                    ? BlogDetailPageStyle.blogContentListItemMobile
                                                    : BlogDetailPageStyle.blogContentListItem
                                            }>{listItem.data}</ListItem>)
                                        }
                                    </List>
                                }
                            })}
                        </Box>
                    </Grid>
                </Grid>
                <Grid container md={12} sx={{ alignItems: "center" }}>
                    <Box
                        sx={
                            isMobile
                                ? BlogDetailPageStyle.moreContainerMobile
                                : BlogDetailPageStyle.moreContainer
                        }
                    >
                        <Grid container md={12}>
                            <Typography
                                sx={
                                    isMobile
                                        ? BlogDetailPageStyle.sectionTitleMobile
                                        : BlogDetailPageStyle.sectionTitle
                                }
                            >
                                {t("blog.more")}
                            </Typography>
                        </Grid>
                        <Grid
                            container
                            md={12}
                            sx={
                                isMobile
                                    ? BlogDetailPageStyle.morePostsMobile
                                    : BlogDetailPageStyle.morePosts
                            }
                            spacing={isMobile ? 4 : 8}
                        >
                            {blogMorePost.map((item) => (
                                <Grid item md={4} sm={12}>
                                    <BlogArticleCard
                                        item={item}
                                        onLearnMore={() => {
                                            navigate(`/news/${item.id}/detail`);
                                        }}
                                    />
                                </Grid>
                            ))}
                        </Grid>
                    </Box>
                </Grid>
            </PageContent>
        </>
    );
};

export default BlogDetailPage;
